import QRCode from 'qrcode';

/**
 * El QR de una invitación, con la K de Kodica en el centro.
 *
 * ## Por qué la corrección de errores se queda en M
 *
 * Lo natural al tapar el centro de un QR es subir la corrección de errores, y
 * eso fue lo primero que se hizo aquí. **Medido, resultó al revés.**
 *
 * Se generó este mismo código con un token de verdad, se desenfocó en pasos de
 * un píxel y se volvió a decodificar hasta que dejó de leerse. Ese número —
 * cuánto desenfoque aguanta— es lo que de verdad importa en la reja, donde el
 * visitante enseña la pantalla a pulso, con reflejo y a veces sucia:
 *
 *   · M sin logo: 61×61 módulos, aguanta 4 px
 *   · M con la K: 61×61 módulos, aguanta 4 px
 *   · Q con la K: 73×73 módulos, aguanta 3 px
 *   · H con la K: 81×81 módulos, aguanta 3 px
 *
 * **La K no le cuesta nada a M**, porque la placa ocupa el 26 % del ancho —
 * menos del 7 % del área— y va justo en el centro, donde no hay ningún patrón
 * de localización. Subir a Q mete un 20 % más de módulos para el mismo
 * contenido, cada módulo se hace más pequeño, y lo que se gana en tolerancia a
 * que falten datos se pierde con creces en tolerancia a que la foto salga
 * movida. Que es el fallo que ocurre de verdad.
 *
 * ## Por qué la K se dibuja y no se carga
 *
 * Porque el resultado tiene que salir por `toDataURL()` para poder compartirse,
 * y un lienzo se contamina si le pintas una imagen que el navegador considera
 * de otro origen — entonces `toDataURL()` lanza y el botón de compartir se
 * rompe sin decir por qué. Dibujando los tres polígonos del isotipo no hay
 * imagen que cargar, ni espera, ni nada que se pueda contaminar.
 */

/** Los tres polígonos de `assets/marca/kodica-isotipo.svg`, en su viewBox. */
const ISOTIPO = {
  ancho: 208.786,
  alto: 207,
  poligonos: [
    [
      [3.0, 3.0],
      [58.246, 3.0],
      [58.246, 42.423],
      [3.0, 101.81],
    ],
    [
      [17.738, 103.036],
      [65.641, 51.452],
      [80.799, 65.774],
      [136.948, 3.0],
      [205.786, 3.0],
      [116.944, 105.258],
      [205.786, 204.0],
      [137.763, 204.0],
      [81.208, 143.194],
      [65.232, 156.698],
    ],
    [
      [3.0, 106.722],
      [58.246, 166.901],
      [58.246, 204.0],
      [3.0, 204.0],
    ],
  ],
} as const;

const CIAN = '#01A4EF';

/** Un rectángulo con las esquinas redondeadas, sin depender de `roundRect`. */
function placa(ctx: CanvasRenderingContext2D, x: number, y: number, lado: number, radio: number) {
  ctx.beginPath();
  ctx.moveTo(x + radio, y);
  ctx.arcTo(x + lado, y, x + lado, y + lado, radio);
  ctx.arcTo(x + lado, y + lado, x, y + lado, radio);
  ctx.arcTo(x, y + lado, x, y, radio);
  ctx.arcTo(x, y, x + lado, y, radio);
  ctx.closePath();
}

/**
 * Devuelve el PNG en `data:` listo para enseñar y para compartir.
 *
 * `lado` es la resolución del archivo, no el tamaño en pantalla: se genera al
 * doble de lo que se enseña para que no se vea borroso en una pantalla densa y
 * para que aguante que alguien lo amplíe antes de escanearlo.
 */
export async function qrConLogo(texto: string, lado = 640): Promise<string> {
  const lienzo = document.createElement('canvas');
  lienzo.width = lado;
  lienzo.height = lado;

  await QRCode.toCanvas(lienzo, texto, {
    width: lado,
    // Margen 1 y no 0: la «zona tranquila» es parte del estándar y sin ella hay
    // lectores que no encuentran el código aunque esté perfecto.
    margin: 1,
    // M, y está medido: ver la nota de arriba antes de subirlo.
    errorCorrectionLevel: 'M',
  });

  const ctx = lienzo.getContext('2d');
  // Sin contexto no hay logo, pero sí QR: vale más un código sin marca que
  // ninguno. Pasa en entornos sin canvas, como algunas pruebas.
  if (!ctx) return lienzo.toDataURL('image/png');

  // La placa blanca va debajo del logo y es lo que de verdad tapa módulos. Se
  // dibuja aunque el fondo ya sea blanco: separa la K de la trama y es lo que
  // hace que el lector vea un hueco limpio en vez de ruido.
  const ladoPlaca = lado * 0.26;
  const x = (lado - ladoPlaca) / 2;
  ctx.fillStyle = '#FFFFFF';
  placa(ctx, x, x, ladoPlaca, ladoPlaca * 0.18);
  ctx.fill();

  // El isotipo, encajado en la placa con aire alrededor.
  const ladoLogo = ladoPlaca * 0.72;
  const escala = Math.min(ladoLogo / ISOTIPO.ancho, ladoLogo / ISOTIPO.alto);
  ctx.save();
  ctx.translate((lado - ISOTIPO.ancho * escala) / 2, (lado - ISOTIPO.alto * escala) / 2);
  ctx.scale(escala, escala);
  ctx.fillStyle = CIAN;
  for (const poligono of ISOTIPO.poligonos) {
    ctx.beginPath();
    poligono.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  return lienzo.toDataURL('image/png');
}
